/**
 * AccordionInCanvas — F12.N E3.T1 (Sub-tura 2b).
 *
 * Renderuje akordeon w canvasie buildera. Każdy panel akordeonu zachowuje się
 * jak osobna sekcja — ma własny tytuł, klikalny nagłówek z chevronem, własny
 * worek dzieci oraz JEST drop targetem dla pól/elementów z palety (przez
 * `useDroppable` z `data.target='section'` + `sectionId=panel.id`, kontrakt
 * z `usePaletteDnD`).
 *
 * CRUD itemów akordeonu:
 *  - „+ Dodaj sekcję" — `onAddItem()` (parent dorzuca nowy AccordionItem do drzewa)
 *  - pencil — inline edit tytułu (Enter zapisuje, Escape anuluje)
 *  - kosz — usuwa panel; gdy panel ma dzieci, otwiera `ConfirmDialog` (variant=danger)
 *
 * Wzorzec SellCall: każdy panel = osobny container, ta sama infrastruktura DnD
 * co sekcje. Patrz `docs/sellcall/01-layout-builder.md §6`.
 */

import {
  type CSSProperties,
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, ChevronDown, Pencil, Plus, Trash2, X as XIcon } from 'lucide-react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useAutoOpenOnDragOver } from '@/hooks/useAutoOpenOnDragOver';
import type { AccordionItem, LayoutElement } from '@/lib/layout-tree';

import { DragHandle } from './DragHandle';

export interface AccordionInCanvasProps {
  element: LayoutElement;
  /**
   * Renderuje pojedyncze dziecko panelu — parent dispatch'uje (field / nested element).
   * `containerId` to id panelu (`AccordionItem.id`), używane przez parent do wpięcia
   * `useSortable` z `sortableContainerId={containerId}` (Pakiet B Sub-2).
   */
  renderChild: (child: LayoutElement, containerId: string) => ReactNode;
  /** Dodaje nowy AccordionItem do akordeonu. */
  onAddItem: () => void;
  /** Aktualizuje tytuł istniejącego panelu. */
  onUpdateItemTitle: (panelId: string, newTitle: string) => void;
  /** Usuwa panel (z dziećmi). Parent zarządza dalszą akcją po confirm. */
  onRemoveItem: (panelId: string) => void;
  /**
   * Fabryka prop'ów dla DragHandle panelu (Sub-tura 2 pakietu B).
   * Parent zwraca spread z `useSortable.attributes` + `listeners` per panelId.
   * Bez tego prop'a panel ma handle wizualny ale gest jest nieaktywny (Sub-tura 1).
   */
  panelDragHandlePropsFor?: (panelId: string) => HTMLAttributes<HTMLDivElement>;
}

export function AccordionInCanvas({
  element,
  renderChild,
  onAddItem,
  onUpdateItemTitle,
  onRemoveItem,
  panelDragHandlePropsFor,
}: AccordionInCanvasProps): ReactNode {
  const { t } = useTranslation();
  const items = element.accordionItems ?? [];
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(items.filter((i) => i.defaultExpanded).map((i) => i.id)),
  );
  const [pendingDelete, setPendingDelete] = useState<AccordionItem | null>(null);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRemoveClick = (item: AccordionItem) => {
    if (item.children.length === 0) {
      onRemoveItem(item.id);
    } else {
      setPendingDelete(item);
    }
  };

  return (
    <div data-testid={`accordion-canvas-${element.id}`}>
      <div className="divide-y divide-border-primary rounded-lg border border-border-primary">
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {items.map((item) => (
            <AccordionPanelInCanvas
              key={item.id}
              item={item}
              accordionId={element.id}
              isOpen={expanded.has(item.id)}
              onToggle={() => toggle(item.id)}
              onCommitTitle={(newTitle) => onUpdateItemTitle(item.id, newTitle)}
              onRemove={() => handleRemoveClick(item)}
              renderChild={renderChild}
              dragHandleProps={panelDragHandlePropsFor?.(item.id)}
            />
          ))}
        </SortableContext>
      </div>
      <button
        type="button"
        onClick={onAddItem}
        className="mt-2 inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-accent-600 hover:bg-accent-50 dark:hover:bg-accent-900/20"
        data-testid={`accordion-canvas-${element.id}-add-item`}
      >
        <Plus className="h-3 w-3" />
        {t('tenant.systemModuleBuilder.accordion.addPanel')}
      </button>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.systemModuleBuilder.accordion.confirmDeleteTitle')}
        message={
          pendingDelete
            ? t(
                pendingDelete.children.length === 1
                  ? 'tenant.systemModuleBuilder.accordion.confirmDeleteMessageOne'
                  : 'tenant.systemModuleBuilder.accordion.confirmDeleteMessageOther',
                { title: pendingDelete.title, count: pendingDelete.children.length },
              )
            : ''
        }
        variant="danger"
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) onRemoveItem(pendingDelete.id);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

interface AccordionPanelInCanvasProps {
  item: AccordionItem;
  /** ID akordeonu-rodzica — używany do `AccordionPanelDragData.accordionId`. Pakiet B Sub-2. */
  accordionId: string;
  isOpen: boolean;
  onToggle: () => void;
  onCommitTitle: (newTitle: string) => void;
  onRemove: () => void;
  renderChild: (child: LayoutElement, containerId: string) => ReactNode;
  /** Legacy: fabryka prop'ów dla DragHandle (Sub-1). Sub-2 nadpisuje przez useSortable. */
  dragHandleProps?: HTMLAttributes<HTMLDivElement>;
}

function AccordionPanelInCanvas({
  item,
  accordionId,
  isOpen,
  onToggle,
  onCommitTitle,
  onRemove,
  renderChild,
  dragHandleProps,
}: AccordionPanelInCanvasProps): ReactNode {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.title);

  // Pakiet B Sub-2 — panel akordeonu jest sortowalny w obrębie tego samego akordeonu.
  // `source: 'accordion-panel'` rozpoznawane przez `usePaletteDnD.onDragEnd` (ścieżka 4).
  const sortable = useSortable({
    id: item.id,
    data: { source: 'accordion-panel', accordionId, panelId: item.id },
  });
  const sortableStyle: CSSProperties = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
    opacity: sortable.isDragging ? 0.5 : undefined,
  };
  // Sub-2 nadpisuje legacy dragHandleProps Sub-1 — useSortable to source of truth gestu.
  const effectiveHandleProps = {
    ...sortable.attributes,
    ...sortable.listeners,
    ...(dragHandleProps ?? {}),
  };

  // Kontrakt z usePaletteDnD: panel jest drop targetem z tym samym shape co sekcja.
  // Osobny `useDroppable` na CONTENT panelu (nie nagłówku), bo content może chłonąć
  // pola/elementy z palety, a nagłówek jest sortable source dla panelu samego.
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `${item.id}-drop`,
    data: { source: 'section', target: 'section', sectionId: item.id },
  });

  // Pakiet B Sub-4 — nagłówek jest dodatkowo droppable żeby auto-open zadziałał
  // ze zwiniętego panelu (content jest wtedy ukryty, więc setDropRef nie istnieje).
  // Hover-drag nad nagłówkiem ≥500ms → onToggle (rozwija panel).
  const { setNodeRef: setHeaderDropRef, isOver: isHeaderOver } = useDroppable({
    id: `${item.id}-header-drop`,
    data: { source: 'section', target: 'section', sectionId: item.id },
  });
  useAutoOpenOnDragOver(isHeaderOver, isOpen, onToggle);

  const startEdit = () => {
    setDraft(item.title);
    setEditing(true);
  };

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed.length > 0 && trimmed !== item.title) {
      onCommitTitle(trimmed);
    }
    setEditing(false);
  };

  const cancel = () => {
    setDraft(item.title);
    setEditing(false);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  return (
    <div
      ref={sortable.setNodeRef}
      style={sortableStyle}
      data-testid={`accordion-canvas-panel-${item.id}`}
    >
      <div
        ref={setHeaderDropRef}
        className="flex items-center gap-2 px-3 py-2 hover:bg-bg-secondary"
        data-testid={`accordion-canvas-panel-${item.id}-header-drop`}
      >
        <DragHandle
          ariaLabel={t('tenant.systemModuleBuilder.accordion.dragPanel', { title: item.title })}
          dragHandleProps={effectiveHandleProps as HTMLAttributes<HTMLDivElement>}
          className="p-0.5"
        />
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={`Rozwiń ${item.title}`}
          className="flex flex-1 items-center gap-2 text-left text-sm font-medium text-text-primary"
        >
          <ChevronDown
            className={`h-4 w-4 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
          {!editing && <span>{item.title}</span>}
        </button>

        {editing ? (
          <>
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              autoFocus
              aria-label={t('tenant.systemModuleBuilder.accordion.titleInput')}
              className="flex-1 rounded border border-border-primary bg-bg-primary px-2 py-1 text-sm text-text-primary"
              data-testid={`accordion-canvas-panel-${item.id}-title-input`}
            />
            <button
              type="button"
              onClick={commit}
              aria-label={t('tenant.systemModuleBuilder.accordion.saveTitle')}
              className="rounded p-1 text-text-muted hover:bg-bg-tertiary hover:text-accent-600"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={cancel}
              aria-label={t('tenant.systemModuleBuilder.accordion.cancelEdit')}
              className="rounded p-1 text-text-muted hover:bg-bg-tertiary"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={startEdit}
              aria-label={t('tenant.systemModuleBuilder.accordion.editPanelTitle', {
                title: item.title,
              })}
              className="rounded p-1 text-text-muted opacity-60 hover:bg-bg-tertiary hover:text-accent-600 hover:opacity-100"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onRemove}
              aria-label={t('tenant.systemModuleBuilder.accordion.removePanel', {
                title: item.title,
              })}
              className="rounded p-1 text-text-muted opacity-60 hover:bg-red-50 hover:text-red-600 hover:opacity-100 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>

      {isOpen && (
        <div
          ref={setDropRef}
          className={`border-t border-border-primary p-3 ${
            isOver ? 'bg-accent-50/50 ring-2 ring-dashed ring-accent-400 dark:bg-accent-900/20' : ''
          }`}
          data-testid={`accordion-canvas-panel-${item.id}-drop`}
        >
          {item.children.length > 0 ? (
            <div className="space-y-2">
              <SortableContext
                items={item.children.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
              >
                {item.children.map((child) => (
                  <div key={child.id}>{renderChild(child, item.id)}</div>
                ))}
              </SortableContext>
            </div>
          ) : (
            <p className="rounded border border-dashed border-border-primary px-3 py-4 text-center text-xs text-text-tertiary">
              {t('tenant.systemModuleBuilder.accordion.emptyPanel')}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
